import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getBestAvailableUrl } from '@components/shared/OptimizedImage';
import { prepareDesktopSlidesData } from '@components/shared/ImageViewer/imageViewerUtils';
import { EDITORIAL_GALLERY_MESSAGE } from '@/blocks/preview';

export function postEditorial(payload) {
    if (typeof window === 'undefined' || window.parent === window) return;
    window.parent.postMessage({ type: EDITORIAL_GALLERY_MESSAGE, ...payload }, window.location.origin);
}

function thumbUrl(file) {
    return getBestAvailableUrl(file, 480) || getBestAvailableUrl(file, 768) || file?.url?.original || '';
}

function isSpreadEntry(item) {
    return Array.isArray(item) && item.length === 2;
}

function pairAt(images, index) {
    if (!Array.isArray(images) || index < 0 || index >= images.length - 1) return null;
    const left = images[index];
    const right = images[index + 1];
    if (isSpreadEntry(left) || isSpreadEntry(right)) return null;
    const next = [...images];
    next.splice(index, 2, [left, right]);
    return next;
}

function unpairAt(images, index) {
    if (!Array.isArray(images) || index < 0 || index >= images.length) return null;
    const item = images[index];
    if (!isSpreadEntry(item)) return null;
    const next = [...images];
    next.splice(index, 1, ...item);
    return next;
}

/**
 * Appearance-only filmstrip for editorial (slideshow) pages.
 * Select a frame, drag to reorder, pair/unpair spreads, or open Arrange.
 */
export default function EditorialFilmstrip({
    images,
    files,
    pagePath,
    pages,
    currentImageId,
    onSelect,
    spreadGap = 0,
}) {
    const slides = useMemo(
        () => prepareDesktopSlidesData(images || [], files || {}, pagePath || null, pages || {}).filter(Boolean),
        [images, files, pagePath, pages],
    );
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);
    const [extraIndex, setExtraIndex] = useState(null);
    const dragRef = useRef(null);

    if (!slides.length) return null;

    const activeIndex = slides.findIndex((slide) => {
        if (slide.type === 'spread') return slide.images.some((img) => img.id === currentImageId);
        return slide.id === currentImageId;
    });

    const activeSlide = activeIndex >= 0 ? slides[activeIndex] : null;
    const isActiveSpread = activeSlide?.type === 'spread';
    const canPairWithNext = activeIndex >= 0
        && activeSlide?.type !== 'spread'
        && slides[activeIndex + 1]
        && slides[activeIndex + 1].type !== 'spread';
    const selectedPair = (() => {
        if (extraIndex == null || activeIndex < 0) return null;
        const a = Math.min(activeIndex, extraIndex);
        const b = Math.max(activeIndex, extraIndex);
        if (b - a !== 1) return null;
        if (slides[a]?.type === 'spread' || slides[b]?.type === 'spread') return null;
        return a;
    })();
    const canPair = selectedPair != null || canPairWithNext;
    const pairIndex = selectedPair != null ? selectedPair : (canPairWithNext ? activeIndex : -1);

    const finish = () => {
        const from = dragRef.current;
        const to = overIndex;
        dragRef.current = null;
        setDragIndex(null);
        setOverIndex(null);
        if (from == null || to == null || from === to) return;
        const next = [...(images || [])];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        postEditorial({ action: 'order', images: next });
    };

    const publishImages = (next) => {
        if (!next) return;
        setExtraIndex(null);
        postEditorial({ action: 'order', images: next });
    };

    return (
        <div
            data-editorial-filmstrip
            className="pointer-events-auto fixed bottom-3 left-1/2 z-[10050] flex max-w-[min(980px,calc(100vw-1.5rem))] -translate-x-1/2 items-end gap-2 rounded-xl border border-purple-200 bg-white/95 p-2 shadow-lg backdrop-blur"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="flex max-w-[min(620px,calc(100vw-14rem))] items-end gap-1.5 overflow-x-auto pb-0.5">
                {slides.map((slide, index) => {
                    const active = index === activeIndex;
                    const marked = extraIndex === index;
                    const dropMark = overIndex === index && dragIndex != null && dragIndex !== index;
                    const left = slide.type === 'spread' ? slide.images[0] : slide;
                    const right = slide.type === 'spread' ? slide.images[1] : null;
                    const selectId = slide.type === 'spread' ? slide.images[0]?.id : slide.id;
                    return (
                        <button
                            key={slide.id || index}
                            type="button"
                            draggable
                            title={
                                slide.type === 'spread'
                                    ? 'Spread — click to select, ⌘/Ctrl-click to multi-select, drag to reorder'
                                    : 'Click to select, ⌘/Ctrl-click with neighbour to pair, drag to reorder'
                            }
                            onDragStart={(event) => {
                                dragRef.current = index;
                                setDragIndex(index);
                                event.dataTransfer.effectAllowed = 'move';
                                event.dataTransfer.setData('text/plain', String(index));
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setOverIndex(index);
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                finish();
                            }}
                            onDragEnd={finish}
                            onClick={(event) => {
                                if (event.metaKey || event.ctrlKey) {
                                    if (index === activeIndex) {
                                        setExtraIndex(null);
                                        return;
                                    }
                                    setExtraIndex(index);
                                    return;
                                }
                                setExtraIndex(null);
                                if (selectId) onSelect?.(selectId, slide.url);
                            }}
                            className={`relative flex h-14 shrink-0 overflow-hidden rounded border bg-gray-100 transition ${
                                active ? 'border-purple-600 ring-2 ring-purple-300' : 'border-gray-200 hover:border-purple-300'
                            } ${marked ? 'ring-2 ring-amber-400' : ''} ${dropMark ? 'ring-2 ring-purple-400' : ''} ${dragIndex === index ? 'opacity-40' : ''}`}
                            style={{ width: right ? 88 : 44 }}
                        >
                            <img
                                src={thumbUrl(left?.file)}
                                alt=""
                                className="h-full object-cover"
                                style={{ width: right ? '50%' : '100%' }}
                                draggable={false}
                            />
                            {right && (
                                <img
                                    src={thumbUrl(right.file)}
                                    alt=""
                                    className="h-full w-1/2 object-cover"
                                    draggable={false}
                                />
                            )}
                            <span className="absolute bottom-0 right-0 rounded-tl bg-black/55 px-1 text-[9px] text-white">
                                {index + 1}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="flex shrink-0 flex-col gap-1">
                <div className="flex items-center gap-1">
                    {canPair && (
                        <button
                            type="button"
                            data-action-button
                            onClick={() => publishImages(pairAt(images, pairIndex))}
                            className="rounded-lg border border-purple-200 bg-white px-2 py-1.5 text-[11px] font-medium text-purple-700 hover:bg-purple-50"
                            title={selectedPair != null ? 'Pair the two selected frames' : 'Pair this frame with the next'}
                        >
                            Pair
                        </button>
                    )}
                    {isActiveSpread && (
                        <button
                            type="button"
                            data-action-button
                            onClick={() => publishImages(unpairAt(images, activeIndex))}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                            title="Split this spread into two frames"
                        >
                            Unpair
                        </button>
                    )}
                    <button
                        type="button"
                        data-action-button
                        onClick={() => postEditorial({ action: 'arrange', focusIndex: activeIndex >= 0 ? activeIndex : 0 })}
                        className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        title="Edit crops, scale, and captions in the full sequence editor"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        data-action-button
                        onClick={() => postEditorial({ action: 'arrange' })}
                        className="rounded-lg bg-purple-600 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-purple-700"
                        title="Open the full sequence editor"
                    >
                        Arrange
                    </button>
                </div>
                <label className="flex items-center gap-1.5 px-0.5 text-[10px] text-gray-500" title="Gap between the two pages of a spread">
                    <span className="shrink-0">Spread gap</span>
                    <input
                        type="number"
                        min={0}
                        max={10}
                        value={spreadGap ?? 0}
                        data-action-button
                        onChange={(event) => {
                            const value = Math.max(0, Math.min(10, Number(event.target.value) || 0));
                            postEditorial({ action: 'spreadGap', spreadGap: value });
                        }}
                        className="w-10 rounded border border-gray-200 bg-white px-1 py-0.5 text-[11px] text-gray-800"
                    />
                </label>
            </div>
        </div>
    );
}

EditorialFilmstrip.propTypes = {
    images: PropTypes.array,
    files: PropTypes.object,
    pagePath: PropTypes.string,
    pages: PropTypes.object,
    currentImageId: PropTypes.string,
    onSelect: PropTypes.func,
    spreadGap: PropTypes.number,
};
